import camelCase from "lodash/camelCase";
import { Accordion } from "PFComponents/accordion";
import { ActionIcon } from "PFComponents/action_icon";
import { InlineCalendar } from "PFComponents/calendar/inline_calendar";
import { Inline, Stack } from "PFComponents/containers";
import { SelectV2 } from "PFComponents/select_v2";
import { Tile } from "PFComponents/tile";
import { useModalRegionRef } from "PFCore/hooks/use_modal_region_ref";
import { fetchProfilesOptions } from "PFCore/services/profile";
import { BasicProfile, Option } from "PFCore/types";
import { useTranslation } from "react-i18next";
import { humanize } from "underscore.string";

import {
  ActivityHistoryFilters,
  EMPTY_ACTIVITY_HISTORY_FILTERS,
  useActivityHistoryContext
} from "../../context";
import { hasActiveFilters } from "../../helpers";
import { useAvailableEventTypes } from "../../hooks/use_available_event_types";
import { useHistoryTabEvents } from "../../hooks/use_history_tab_events";
import css from "./history_filters_tile.module.scss";

export const HistoryFiltersTile = () => {
  const { t } = useTranslation("activities", { keyPrefix: "show.history.filters" });
  const { t: tCategory } = useTranslation("notificationsSettings", { keyPrefix: "category" });
  const { filters, setFilters } = useActivityHistoryContext();
  const modalRegionRef = useModalRegionRef();

  const { data: historyData } = useHistoryTabEvents();
  const availableEventTypes = useAvailableEventTypes();
  const availableOperatorIds = historyData?.meta.availableOperatorIds ?? [];

  const updateFilter = <K extends keyof ActivityHistoryFilters>(key: K, value: ActivityHistoryFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const hasAnyFilter = hasActiveFilters(filters);

  const eventTypeOptions = availableEventTypes
    .map<Option<string>>((value) => ({
      id: value,
      value: tCategory(camelCase(value) as unknown as TemplateStringsArray, {
        defaultValue: humanize(value)
      }),
      original: value
    }))
    .sort((optionA, optionB) => optionA.value.localeCompare(optionB.value));

  return (
    <Tile variant="light" paddingVariant="lg" className={css.root}>
      <Accordion
        header={
          <Inline as="span" justifyContent="space-between" alignItems="center" grow={1}>
            {t("title")}
            {hasAnyFilter && (
              <ActionIcon
                name="filter-clean"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  setFilters(EMPTY_ACTIVITY_HISTORY_FILTERS);
                }}
                title={t("clearAll")}
              />
            )}
          </Inline>
        }
        headerVariant="bodyBold"
        iconSet="chevron"
        headerFullWidth
      >
        <Stack gap="spacingLg">
          <SelectV2<BasicProfile>
            label={t("operator.label")}
            multiple
            value={filters.operatorId}
            fetchOptions={({ searchTerm, ids }) =>
              fetchProfilesOptions({ term: searchTerm, value: ids ?? availableOperatorIds }).then((data) => ({
                ...data,
                entries: data.entries.map((profile) => ({
                  id: profile.id,
                  value: profile.text,
                  original: profile
                }))
              }))
            }
            onChange={(options: Option<BasicProfile>[]) =>
              updateFilter(
                "operatorId",
                options.map((option) => option.id)
              )
            }
            onClear={filters.operatorId.length ? () => updateFilter("operatorId", []) : undefined}
          />
          <SelectV2<string>
            label={t("eventType.label")}
            multiple
            value={filters.eventType}
            options={eventTypeOptions}
            onChange={(options: Option<string>[]) =>
              updateFilter(
                "eventType",
                options.map((option) => option.original)
              )
            }
            onClear={filters.eventType.length ? () => updateFilter("eventType", []) : undefined}
          />
          <InlineCalendar
            label={t("createdAt.start")}
            selectedDate={filters.createdAtMin}
            maxDate={filters.createdAtMax ?? undefined}
            handleChange={(date) => updateFilter("createdAtMin", date)}
            portalRef={modalRegionRef}
            letClear
          />
          <InlineCalendar
            label={t("createdAt.end")}
            selectedDate={filters.createdAtMax}
            minDate={filters.createdAtMin ?? undefined}
            handleChange={(date) => updateFilter("createdAtMax", date)}
            portalRef={modalRegionRef}
            letClear
          />
        </Stack>
      </Accordion>
    </Tile>
  );
};
